// IPC transport. Replaces the browser WebSocket with a MessagePort
// connection to a SharedWorker that hosts an in-browser broker. Same
// envelope shape as the WS transport — ControllerSocket never knows
// the difference.
//
// Two factory builders here:
//
//   sharedWorkerTransportFactory(workerCtor) — production path. The
//     caller passes a SharedWorker constructor (typically produced
//     by Vite's `?sharedworker` import) so the worker URL is resolved
//     at build time and bundled correctly.
//
//   inProcessTransportFactory(broker)        — test / non-worker path.
//     Wires ControllerSocket straight to an in-process InMemoryBroker
//     via a synthetic MessageChannel-style pair. Used by unit tests
//     and by the dev-mode fallback when SharedWorker is unavailable.

import type { Envelope } from './controller_socket';
import { bindPort } from './broker/shared_worker_entry';
import type { InMemoryBroker } from './broker/in_memory_broker';
import type { Transport, TransportEvent, TransportFactory } from './transport';

interface IpcFrame {
  kind: 'envelope' | 'close';
  env?: Envelope;
  code?: number;
  reason?: string;
}

/**
 * SharedWorker-backed factory. Pass a thunk that returns a fresh
 * SharedWorker instance — typically a one-liner around
 * `new SharedWorker(new URL('./worker.ts', import.meta.url),
 * { type: 'module' })` so the bundler can resolve the worker URL at
 * build time. The thunk is invoked at most once per factory.
 */
export function sharedWorkerTransportFactory(
  spawn: () => SharedWorker,
): TransportFactory {
  let worker: SharedWorker | null = null;
  return (url, listener) => {
    if (!worker) worker = spawn();
    const port = worker.port;
    return wirePort(url, port, listener);
  };
}

/**
 * In-process factory. Each call creates a fresh MessageChannel-style
 * pair and binds one end to the broker. Used in tests and in dev mode
 * when no SharedWorker exists (single-tab only — no cross-tab sync).
 */
export function inProcessTransportFactory(broker: InMemoryBroker): TransportFactory {
  return (url, listener) => {
    // Always use the synthetic port pair here so timing is
    // microtask-deterministic: the in-process factory exists for tests
    // and for the SharedWorker-less fallback, and both want predictable
    // ordering rather than the real MessageChannel's task-queue
    // dispatch. The SharedWorker factory uses real MessagePorts.
    const { brokerPort, clientPort } = makeSyntheticPortPair();
    bindPort(broker, brokerPort);
    return wirePort(url, clientPort, listener);
  };
}

function wirePort(
  url: string,
  port: MessagePort,
  listener: (e: TransportEvent) => void,
): Transport {
  let closed = false;
  port.addEventListener('message', (ev: MessageEvent<IpcFrame>) => {
    const frame = ev.data;
    if (!frame || typeof frame !== 'object') return;
    if (frame.kind === 'close') {
      if (closed) return;
      closed = true;
      listener({ type: 'close', code: frame.code ?? 1000, reason: frame.reason ?? '' });
      return;
    }
    if (frame.kind === 'envelope' && frame.env) {
      listener({ type: 'message', data: JSON.stringify(frame.env) });
    }
  });
  port.start();
  // Open is delivered on the next microtask so the caller has a chance
  // to wire pending state. Matches the timing of the WS transport.
  queueMicrotask(() => listener({ type: 'open' }));
  return {
    url,
    send: (text: string) => {
      if (closed) return;
      let env: Envelope;
      try {
        env = JSON.parse(text) as Envelope;
      } catch (err) {
        listener({
          type: 'error',
          reason: 'malformed outbound envelope: ' + (err as Error).message,
        });
        return;
      }
      const frame: IpcFrame = { kind: 'envelope', env };
      try {
        port.postMessage(frame);
      } catch (err) {
        listener({ type: 'error', reason: (err as Error).message });
      }
    },
    close: (code, reason) => {
      if (closed) return;
      closed = true;
      const frame: IpcFrame = { kind: 'close', code, reason };
      try {
        port.postMessage(frame);
      } catch {
        // ignore
      }
      try {
        port.close();
      } catch {
        // ignore
      }
    },
  };
}

/**
 * Synthetic MessagePort pair. Each `postMessage` on one port dispatches
 * to the other's `message` listeners on the next microtask, which is
 * enough to satisfy the broker bridge and tests without depending on a
 * global MessageChannel or its task-queue timing semantics.
 */
function makeSyntheticPortPair(): { brokerPort: MessagePort; clientPort: MessagePort } {
  const a = new SyntheticPort();
  const b = new SyntheticPort();
  a.bind(b);
  b.bind(a);
  return {
    brokerPort: a as unknown as MessagePort,
    clientPort: b as unknown as MessagePort,
  };
}

class SyntheticPort {
  private peer: SyntheticPort | null = null;
  private listeners = new Set<(ev: { data: unknown }) => void>();
  private started = false;
  private queue: unknown[] = [];

  bind(peer: SyntheticPort): void {
    this.peer = peer;
  }

  postMessage(msg: unknown): void {
    if (!this.peer) return;
    this.peer.enqueue(msg);
  }

  addEventListener(type: string, listener: (ev: { data: unknown }) => void): void {
    if (type !== 'message') return;
    this.listeners.add(listener);
  }

  removeEventListener(type: string, listener: (ev: { data: unknown }) => void): void {
    if (type !== 'message') return;
    this.listeners.delete(listener);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    const drain = this.queue;
    this.queue = [];
    for (const msg of drain) this.dispatch(msg);
  }

  close(): void {
    this.peer = null;
    this.listeners.clear();
  }

  private enqueue(msg: unknown): void {
    if (!this.started) {
      this.queue.push(msg);
      return;
    }
    queueMicrotask(() => this.dispatch(msg));
  }

  private dispatch(msg: unknown): void {
    for (const l of this.listeners) {
      try {
        l({ data: msg });
      } catch {
        // listener errors do not break the channel
      }
    }
  }
}

/**
 * Convenience: detect ?ipc=1 (or ?ipc) on the current URL. Apps use
 * this to pick the transport factory at boot.
 */
export function ipcModeEnabled(): boolean {
  if (typeof globalThis === 'undefined') return false;
  const w = globalThis as { location?: { search?: string } };
  const search = w.location?.search ?? '';
  if (!search) return false;
  const params = new URLSearchParams(search);
  return params.has('ipc') && params.get('ipc') !== '0';
}
